// shell/panes/list-pane.ts
// Combined list pane factory with view mode switching

import type { PaneType, PaneInstance, PaneTypeRenderContext } from '../core/types';
import { createListPaneContainer } from '../utils/pane-ui';
import { DataListView } from '../components/data-list-view';
import type { DataListViewConfig } from '../components/data-list-view';
import type { DataSource, LiveDataSource } from '../components/data-source';
import type { ListPaneConfig, ListPaneInstanceData } from './types';
import { deriveCapabilities, resolveAriaLabel, derivePersistenceKey, refreshTabBarActionsAfterRender } from './table-pane';

/**
 * Creates a combined list pane type with view mode switching.
 *
 * List panes support all view modes:
 * - Table: Traditional table with headers and sorting
 * - Grid: Card grid with responsive columns
 * - Thumbs: Thumbnail grid with size slider
 *
 * Users can switch between modes via toolbar or keyboard shortcuts.
 * View mode preference is persisted per instance.
 *
 * @example
 * ```typescript
 * const fileListPane = createListPaneType<FileItem>({
 *     typeId: 'file-list',
 *     defaultTitle: 'Files',
 *     createDataSource: (ctx) => new DataSource({
 *         fetch: (params) => fetchFiles(ctx.instance.context.folderId, params),
 *         keyFn: (f) => f.id,
 *         pageSize: 64
 *     }),
 *     columns: fileColumns,
 *     keyFn: (f) => f.id,
 *
 *     // Enable all modes
 *     initialMode: 'table',
 *     enabledModes: ['table', 'grid', 'thumbs'],
 *
 *     // Table options
 *     tableRowHeight: 32,
 *     stickyHeader: true,
 *
 *     // Grid/thumbs options
 *     cardWidth: 200,
 *     thumbSizes: { min: 60, max: 400, default: 120 },
 *
 *     selectionMode: 'multi',
 *     bulkActions: fileActions,
 *     onItemActivate: (file, ctx) => {
 *         if (file.type === 'folder') {
 *             ctx.shell.openPane({
 *                 typeId: 'file-list',
 *                 context: { folderId: file.id }
 *             });
 *         } else {
 *             ctx.shell.openPane({
 *                 typeId: 'file-viewer',
 *                 context: { fileId: file.id }
 *             });
 *         }
 *     },
 *     ariaLabel: 'File browser'
 * });
 *
 * shell.registerPaneType(fileListPane);
 * ```
 */
export function createListPaneType<T>(config: ListPaneConfig<T>): PaneType {
    // Track instances for cleanup
    const instances = new Map<string, ListPaneInstanceData<T>>();

    // Derive capabilities from config
    const capabilities = deriveCapabilities(config);

    // Add view-mode capability for list panes
    if (!capabilities.includes('view-mode')) {
        capabilities.push('view-mode');
    }

    // Per-instance (ctx, dataSource) for the title-row slot, which the surface
    // calls with only the instance. Set in render, cleared in onDestroy.
    const renderState = new WeakMap<PaneInstance, { ctx: PaneTypeRenderContext; dataSource: DataSource<T> | LiveDataSource<T> }>();

    return {
        typeId: config.typeId,
        kind: 'leaf',
        tabBarActions: config.tabBarActions
            ? (instance) => {
                const st = renderState.get(instance);
                return st ? config.tabBarActions!(st.ctx, st.dataSource) : [];
            }
            : undefined,
        defaultTitle: config.defaultTitle,
        capabilities,
        defaultFlags: config.defaultFlags,

        render: (ctx: PaneTypeRenderContext): HTMLElement => {
            // Create data source for this instance
            const dataSource = config.createDataSource(ctx);
            renderState.set(ctx.instance, { ctx, dataSource });

            // Build layout slots
            const header = config.createHeader?.(dataSource, ctx);
            const filterBar = config.createFilterBar?.(dataSource, ctx);
            const toolbar = config.createToolbar?.(dataSource, ctx);

            const { element: container, body } = createListPaneContainer({
                header, filterBar, toolbar,
            });

            // Build DataListView config with all options
            const listViewConfig: DataListViewConfig<T> = {
                container: body,
                dataSource,
                columns: config.columns,
                keyFn: config.keyFn,
                ariaLabel: resolveAriaLabel(config.ariaLabel, ctx.instance),

                // View mode configuration
                initialMode: config.initialMode ?? 'table',
                enabledModes: config.enabledModes ?? ['table', 'grid', 'thumbs'],

                // Table-specific
                tableRowHeight: config.tableRowHeight,
                stickyHeader: config.stickyHeader,

                // Grid-specific
                cardRenderer: config.cardRenderer,
                cardWidth: config.cardWidth,
                gridGap: config.gridGap,
                minCardWidth: config.minCardWidth,
                stretchCards: config.stretchCards,

                // Thumbs-specific
                thumbSizes: config.thumbSizes,

                // Selection
                selectionMode: config.selectionMode,
                bulkActions: config.bulkActions,
                canSelect: config.canSelect,
                selectAllBehavior: config.selectAllBehavior,

                // Features
                enableHeatMap: config.enableHeatMap,
                enableRowStates: config.enableRowStates,

                // Persistence
                persistenceKey: derivePersistenceKey(config.persistenceKeyPrefix, ctx.instance),

                // Events - wire up with context
                onItemSelect: config.onItemSelect
                    ? (item) => {
                        // Persist active key for restoration after page reload
                        ctx.instance.context.__activeKey = config.keyFn(item);
                        config.onItemSelect!(item, ctx);
                    }
                    : undefined,
                onItemActivate: config.onItemActivate
                    ? (item) => config.onItemActivate!(item, ctx)
                    : undefined,
                onSelectionChange: config.onSelectionChange
                    ? (selState) => config.onSelectionChange!(selState, ctx)
                    : undefined,
                onViewModeChange: config.onViewModeChange
                    ? (mode) => config.onViewModeChange!(mode, ctx)
                    : undefined,
                onSortChange: config.onSortChange
                    ? (col, dir) => config.onSortChange!(col, dir, ctx)
                    : undefined,
            };

            // Create DataListView
            const dataListView = new DataListView(listViewConfig);

            // Auto-start LiveDataSource if applicable
            if ('startListening' in dataSource && typeof (dataSource as LiveDataSource<T>).startListening === 'function') {
                (dataSource as LiveDataSource<T>).startListening();
            }

            // Store instance data for cleanup and access
            instances.set(ctx.instance.instanceId, { dataListView, dataSource });

            // For single-selection list panes that open detail panes:
            // clear selection when all panes after us in the flow are closed.
            // Deferred via queueMicrotask because openPane removes the old detail
            // (emitting pane-closed) before adding the new one — without deferral
            // we'd see the list as the last pane and incorrectly clear the selection.
            // Clear active row highlight when detail pane is closed
            if (config.onItemSelect && ctx.surface) {
                const surface = ctx.surface;
                const myInstanceId = ctx.instance.instanceId;
                ctx.disposables.add(ctx.shell.on('pane-closed', (data) => {
                    const { surfaceId } = data as { surfaceId: string };
                    if (surfaceId !== surface.surfaceId) return;
                    queueMicrotask(() => {
                        const panes = surface.getPanes();
                        const myIndex = panes.findIndex(p => p.instanceId === myInstanceId);
                        if (myIndex >= 0 && myIndex === panes.length - 1) {
                            dataListView.clearActiveKey();
                            delete ctx.instance.context.__activeKey;
                        }
                    });
                }));
            }

            // Restore active key from persisted context after data loads
            const restoredKey = ctx.instance.context.__activeKey as string | undefined;
            if (restoredKey && config.onItemSelect) {
                const restoreCallback = config.onItemRestore;
                const tryRestore = () => {
                    const item = dataSource.getItemByKey(restoredKey);
                    if (item) {
                        dataListView.setActiveKey(restoredKey);
                        restoreCallback?.(item, ctx);
                        unsub();
                    }
                };
                const unsub = dataSource.on('change', tryRestore);
                ctx.disposables.add(unsub);
            }

            // Invoke onCreated hook
            config.onCreated?.({ dataListView, dataSource, container }, ctx);

            // The header was built before this render, so the title row is
            // still empty: ask for it now.
            refreshTabBarActionsAfterRender(config, ctx);

            return container;
        },

        onActivate: (instance: PaneInstance) => {
            const data = instances.get(instance.instanceId);
            if (data) {
                // Don't steal focus from interactive children (e.g. search input)
                const paneEl = instance.element;
                if (paneEl && paneEl.contains(document.activeElement) && document.activeElement !== paneEl) {
                    return;
                }
                data.dataListView.focus();
            }
        },

        onDestroy: (instance: PaneInstance) => {
            renderState.delete(instance);
            const data = instances.get(instance.instanceId);
            if (data) {
                data.dataListView.dispose();

                if ('dispose' in data.dataSource && typeof data.dataSource.dispose === 'function') {
                    data.dataSource.dispose();
                }

                instances.delete(instance.instanceId);
            }
        },
    };
}
